import type { ReactNode } from "react";
import React from "react";
import cs from "classnames";

import type { TypographyProps } from "@braintrust/braintrust-ui-components";
import { Typography } from "@braintrust/braintrust-ui-components";
import { FlowerIcon } from "@braintrust/braintrust-ui-components/Icons";
import { Emoji } from "@js/components/emoji";

import {
  mapErrorCodeToUserError,
  mapMessageCodeToUserMessage,
} from "../../utils";

import styles from "./style.module.scss";

type AuthComponentProps = {
  title: string;
  subTitle?: string;
  titleEmoji?: string;
  messageCode?: string;
  errorCode?: string;
  className?: string;
  titleSize?: TypographyProps["size"];
  children: ReactNode;
};

export const AuthComponent = ({
  children,
  className,
  errorCode,
  messageCode,
  title,
  subTitle,
  titleEmoji,
  titleSize = "small",
}: AuthComponentProps) => {
  return (
    <AuthComponentWrapper className={className}>
      {titleEmoji && <AuthEmojiTitle emoji={titleEmoji} />}
      <Typography
        component="h1"
        variant="title"
        size={titleSize}
        fontWeight={400}
        sx={{ mb: subTitle ? 2 : 5 }}
      >
        {title}
      </Typography>

      {subTitle && (
        <Typography component="p" size="large" fontWeight={400} sx={{ mb: 4 }}>
          {subTitle}
        </Typography>
      )}
      <div>
        <AuthComponentErrors messageCode={messageCode} errorCode={errorCode} />
        {children}
      </div>
    </AuthComponentWrapper>
  );
};

export const AuthComponentWrapper = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => {
  return <div className={cs(styles.authForm, className)}>{children}</div>;
};

export const AuthComponentErrors = ({
  errorCode,
  messageCode,
}: {
  errorCode?: string;
  messageCode?: string;
}) => {
  const mappedErrorCode = errorCode
    ? mapErrorCodeToUserError(errorCode)
    : undefined;
  const mappedMessageCode = messageCode
    ? mapMessageCodeToUserMessage(messageCode)
    : undefined;
  const message = mappedErrorCode || mappedMessageCode;
  if (!message) {
    return <></>;
  }

  return <div className={cs("mt0 mb+")}>{message}</div>;
};

export const AuthEmojiTitle = ({ emoji }: { emoji: string }) => {
  return (
    <div className={styles.authTitleEmojiWrapper}>
      <FlowerIcon />
      <Emoji emoji={emoji} size="26px" />
    </div>
  );
};
